import 'reflect-metadata';
import { GetStaticProps } from 'next';
import { container } from 'tsyringe';
import { ContentfulService } from '../../server/services/contentful.service';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { FullPost } from '../../types/shared.types';
import React from 'react';
import { Layout } from '../../components/common/layout/Layout';
import Image from 'next/image';
import { format } from 'date-fns';
import { CustomSEO } from '../../components/common/layout/HeadMeta';

const ArticleBySlug = ({ article, slug }: { article: FullPost; slug: string }) => {
    const seoOverride: CustomSEO = {
        title: article.title,
        description: article.excerpt,
        author: article.postAuthor.name,
        image: article.leadingImage.url,
    };

    return (
        <Layout seoOverride={seoOverride}>
            <div className="article-container">
                <div className="article-image-wrapper">
                    <div className="article-image">
                        <Image
                            width="1000"
                            height="500"
                            objectPosition="center"
                            src={article.leadingImage.url}
                            alt={`${article.title}`}
                            quality={100}
                        />
                    </div>
                </div>
                <h1 className="article-title">{article.title}</h1>
                <p className="article-date">{format(new Date(article.sys.firstPublishedAt), 'LLLL d, y')}</p>
                <div
                    className="article-content"
                    dangerouslySetInnerHTML={{
                        __html: documentToHtmlString(article.content?.json ?? '<p>Please add content</p>'),
                    }}
                />
            </div>
        </Layout>
    );
};

// Adding this makes it pure static
// feed in content entered in by admin console for language terms (will be done in each page)
// a simple key value pairing w/ key -> text
export const getStaticProps: GetStaticProps = async (ctx) => {
    const {
        params: { slug },
    } = ctx as { params: { slug: string } };

    const preview = ctx.preview ?? false;
    const contentfulService = container.resolve(ContentfulService);
    const response = await contentfulService.getPostBySlug(slug, preview);
    if (response) {
        return {
            props: {
                article: response,
                slug: slug,
            },
            revalidate: 120,
        };
    }

    return {
        notFound: true,
    };
};

export async function getStaticPaths() {
    const contentfulService = container.resolve(ContentfulService);
    const response = await contentfulService.getAllPosts();

    return {
        paths:
            response?.postCollection.items.map((p) => ({
                params: { slug: p.slug },
            })) ?? [],
        fallback: 'blocking',
    };
}

export default ArticleBySlug;
